import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { isValidArray } from "utils/array";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { broadcastAccountsRefetch } from "utils/broadcastChannel";

import {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from "app/store/notificationPreference/api/notificationPreferenceApi";
import {
  showPreferenceList,
  toggleAccountFilter,
  toggleEditPreference,
} from "app/store/notificationPreference/slice/notificationPreferenceSlice";
import { ALERT_NOTIFICATION_DEFAULT_PREFERENCES } from "static/constants";

const useNotificationPreferenceList = () => {
  const { accounts } = useSelector((state) => state.account);
  const { selectedAccount, toggleAccount } = useSelector(
    (state) => state.notificationPreference
  );
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditPreference, setIsEditPreference] = useState(false);
  const [isCancelPreference, setIsCancelPreference] = useState(false);
  const [hasNotificationPreference, setHasNotificationPreference] =
    useState(false);

  const [newAccount, setNewAccount] = useState("");
  const [preferences, setPreferences] = useState([]);
  const [originalPreferences, setOriginalPreferences] = useState([]);

  const {
    data,
    isLoading: isPreferenceLoading,
    isFetching,
  } = useGetNotificationPreferencesQuery(
    { account: selectedAccount },
    { refetchOnMountOrArgChange: true }
  );

  const [updateNotificationPreferences] =
    useUpdateNotificationPreferencesMutation();

  const getFormattedPreferences = (preferenceData = []) => {
    const preferenceMap = Object.fromEntries(
      preferenceData.map((p) => [p.alert_type, p.notify_configs])
    );

    return ALERT_NOTIFICATION_DEFAULT_PREFERENCES.map(
      ({ alert_type, notify_configs }) => ({
        alert_type,
        notify_configs: isValidArray(preferenceMap[alert_type])
          ? preferenceMap[alert_type]
          : notify_configs,
      })
    );
  };

  useEffect(() => {
    const formattedPreferences = getFormattedPreferences(
      data?.configurations || []
    );

    const preferenceCopy = structuredClone(formattedPreferences);
    setPreferences([...preferenceCopy]);
    setOriginalPreferences([...preferenceCopy]);
    setHasNotificationPreference(formattedPreferences.length > 0);
  }, [data]);

  useEffect(() => {
    if (!toggleAccount && !isEditPreference) {
      dispatch(
        showPreferenceList({
          showList: true,
          accounts,
          selectedAccount: newAccount || selectedAccount,
        })
      );
    }
  }, [
    dispatch,
    accounts,
    newAccount,
    toggleAccount,
    selectedAccount,
    isEditPreference,
  ]);

  const editPreferences = () => {
    setIsEditPreference(true);
    dispatch(toggleEditPreference(true));
  };

  const cancelEdit = () => {
    setPreferences(originalPreferences);
    setIsEditPreference(false);
    dispatch(toggleEditPreference(false));
    setIsCancelPreference(false);
  };

  const savePreferences = async (configurations) => {
    setIsLoading(true);
    try {
      const configurationsData = (configurations || []).map(
        ({ same_as_red, notify_configs, ...preference }) => ({
          ...preference,
          notify_configs: (notify_configs || []).map(
            ({ same_as_regular, ...config }) => ({
              ...config,
            })
          ),
        })
      );

      const payload = {
        account: selectedAccount,
        configurations: configurationsData,
      };

      const response = await updateNotificationPreferences(payload).unwrap();

      if (!response?.error) {
        showSuccessToast(response?.message);
        broadcastAccountsRefetch();
        const formattedPreferences = getFormattedPreferences(
          response?.data?.configurations || []
        );
        const preferenceCopy = structuredClone(formattedPreferences);

        setPreferences([...preferenceCopy]);
        setOriginalPreferences([...preferenceCopy]);
        setIsEditPreference(false);
        dispatch(toggleEditPreference(false));
      } else {
        showErrorToast(
          response?.message || "Failed to update notification preferences."
        );
      }
    } catch (error) {
      showErrorToast(
        error?.data?.message ||
          error?.message ||
          "Failed to update notification preferences."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const changeNotificationPreference = (event) => {
    setNewAccount(event.target.value);

    if (isEditPreference) {
      dispatch(toggleAccountFilter(true));
    }
  };

  const discardAccountToggle = () => {
    dispatch(toggleAccountFilter(false));
    dispatch(
      showPreferenceList({
        showList: true,
        accounts,
        selectedAccount: newAccount,
      })
    );
  };

  const cancelAccountToggle = () => {
    dispatch(
      showPreferenceList({
        showList: true,
        accounts,
        selectedAccount,
      })
    );
    dispatch(toggleAccountFilter(false));
  };

  return {
    dispatch,
    accounts,
    newAccount,
    preferences,
    selectedAccount,
    isEditPreference,
    isCancelPreference,
    hasNotificationPreference,
    isLoading: isLoading || isPreferenceLoading || isFetching,

    cancelEdit,
    toggleAccount,
    editPreferences,
    savePreferences,
    cancelAccountToggle,
    discardAccountToggle,
    setIsCancelPreference,
    changeNotificationPreference,
  };
};

export default useNotificationPreferenceList;
